import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import { DigitalCatalogueBllService } from "../digital-catalogue-bll.service";
import { PavilionGroupModel, PavilionModel } from "../digital-catalogue.model";
import { CommonModule } from "@angular/common";
import { DeviceTypeService } from "../../../core/services/device-type.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ProfileBllService } from "../../profile/profile-bll.service";
import {
  CategoryModel,
  UserEventStatusRespModel,
} from "../../profile/profile.model";
import {
  APIErrorModel,
  BaseResponse,
} from "../../../shared/models/base-response.model";
import { UserService } from "../../../core/services/user.service";
import { AuthBllService } from "../../auth/auth-bll.service";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { FormsModule } from "@angular/forms";
import { VirtualEventBllService } from "../../virtual-event/virtual-event-bll.service";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { HomeBllService } from "../../home/home-bll.service";
import { EventDetailsResponseModel } from "../../home/home.model";

@Component({
  selector: "app-pavilion-list",
  imports: [HeaderComponent, LeftSideNavComponent, CommonModule, FormsModule, LoaderComponent],
  templateUrl: "./pavilion-list.component.html",
  styleUrls: ["./pavilion-list.component.scss"],
})
export class PavilionListComponent implements OnInit, OnDestroy {
  isMobileDevice: boolean = false;
  pavilionGroups: PavilionGroupModel[] = [];
  allPavilions: PavilionModel[] = []; // Flattened list of all pavilions
  companiesCount: number = 0;
  productsCount: number = 0;
  catalogDetails: boolean = false;
  /**
   * User profile data
   */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;

  // User authentication status
  isAuthenticated: boolean = false;

  searchText: string = "";
  isSearchOpen = false;

  activeTab: "pavilions" | "areaofinterest" = "pavilions";
  selectedCategories: CategoryModel[] = [];
  otherCategories: CategoryModel[] = [];
  /**
   * An array of category models representing the user's selected areas of interest.
   * Used to store and manage the categories relevant to the user's profile.
   */
  areaOfInterest: CategoryModel[] = [];
  /**
   * Stores a list of area of interest identifiers that are prefilled for the user.
   * This array is typically populated based on the user's existing profile data.
   */
  prefilledUserAreaOfInterest: string[] = [];
  /**
   * Stores the list of area of interest IDs that are prefilled for the user.
   * These IDs are used to initialize the user's selected areas of interest in the profile.
   */
  prefilledUserAreaOfInterestIds: string[] = [];

  hasRegisteredAsVirtualVisitor: boolean = false;
  allCategoriesList: CategoryModel[] = [];
  otherCatTitle: string = 'Other Categories';
  catButton: string = 'Edit your Area of Interest';
  refSource: string = '';
  @ViewChild("searchInput") searchInput!: ElementRef;

  searchDebounce: ReturnType<typeof setTimeout> | null = null;

  isVotingActive: boolean = false;
  isContestActive: boolean = false;

  eventDetails: EventDetailsResponseModel | null = null;

  constructor(
    private deviceService: DeviceTypeService,
    private catalogueBllService: DigitalCatalogueBllService,
    private router: Router,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private authService: AuthBllService,
    private loader: LoaderService,
    private route: ActivatedRoute,
    private virtualBll: VirtualEventBllService,
    private homeBll: HomeBllService
  ) { }

  ngOnInit(): void {
    this.isMobileDevice = this.deviceService.isMobile();

    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });

    if (this.isAuthenticated) {
      this.fetchUserEventStatus();
    }

    this.callfetchPavilionList(); // Fetch pavilion list on init
    this.getAreaOfInterestData();
    this.route.queryParams.subscribe((params) => {
      if (params["tab"]) {
        this.activeTab = params["tab"]; // 'areaofinterest'
      }
    });

    this.getRefSourceFromRoute();
    this.loadEventDetails();
  }

  /**
   * @description Fetches the user's event status and updates the user profile and service accordingly.
   */
  fetchUserEventStatus() {
    this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        this.userProfile = res.data.user_profile;
        if (this.userProfile) {
          const previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
          this.userService.setUser({
            name: userName,
            profileImg: previewLogo,
            mobile: this.userProfile.mobile,
            state: this.userProfile.state,
            city: this.userProfile.city,
            hasUnassignedTickets:
              res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets:
              res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 ||
              res.data.event_my_tickets.tickets_assigned_for_me > 0

          });
          this.getAreaOfInterestData();
        }

        /**
         * @description Check if the user is an event attendee and register as a virtual visitor if true.
         */
        const attendee = res?.data?.event_my_tickets?.event_attendee_details;
        if (!attendee) {
          this.registerVirtualVisitor();
        }

      })
      .catch((err: APIErrorModel) => {
        console.error("Error fetching user event status:", err);
      });
  }

  /**
   * Fetches the pavilion list from the BLL service.
   */
  callfetchPavilionList() {
    this.loader.show(); // start loader before API call
    this.catalogueBllService
      .fetchPavilionList()
      .then((response) => {
        this.catalogDetails = true;
        this.companiesCount = response.data.companies_count;
        this.productsCount = response.data.products_count;
        this.pavilionGroups = response.data.pavilion_groups;

        // Flatten all pavilion groups into one unified list
        this.allPavilions = this.pavilionGroups.flatMap(
          (group) => group.pavilions ?? []
        );

        this.loader.hide(); // stop loader after data is set
      })
      .catch((error) => {
        this.catalogDetails = false;
        this.loader.hide(); // stop loader in case of error as well
        if (error.status === 410) {
          this.router.navigate(['/home']);
        }
      });
  }

  /**
   * View the product list for a specific pavilion.
   * @param pavilion The pavilion object containing details.
   */
  viewProductList(pavilion: any) {

    this.catalogueBllService.saveSelectedPavilionDetails(
      pavilion.code,
      pavilion.name,
      pavilion.pavilion_hover_image_url
    );

    this.router.navigate(["/catalogue/products-list"], {
      queryParams: { pavilion_code: pavilion.code },
    });
  }

  /**
   * Handles the back button click event.
   */
  onClickBack(): void {
    this.router.navigate(["/home"]);
    //this.location.back();
  }

  /**
   * Open floating search
   */
  openFloatingSearch() {
    this.isSearchOpen = true;

    // Focus textbox after animation
    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 300);
    }
  }

  /**
   * Close floating search
   */
  closeFloatingSearch() {
    this.isSearchOpen = false;
    this.searchText = "";
  }

  openWishlist(): void {
    this.router.navigate(["/catalogue/product-wishlist"]);
  }

  /**
   * Sets the active tab in the component.
   */
  setActiveTab(tab: "pavilions" | "areaofinterest") {
    this.activeTab = tab;
  }

  /** Fetch area of interest data */
  getAreaOfInterestData() {
    this.profileBll.getAreaOfInterestData()
      .then((res) => {
        this.areaOfInterest = res.data.list;
        this.allCategoriesList = res.data.list;

        const raw = this.userProfile?.category;

        if (raw) {
          // 1. Split & clean category names
          this.prefilledUserAreaOfInterest = raw
            .split(",")
            .map(c => c.trim())
            .filter(c => c !== "");

          const prefilled = this.prefilledUserAreaOfInterest;

          // IMPORTANT: Confirm field is cat.name OR cat.app_name_english
          this.selectedCategories = this.areaOfInterest.filter(cat =>
            prefilled.includes(cat.name)
          );

          this.otherCategories = this.areaOfInterest.filter(cat =>
            !prefilled.includes(cat.name)
          );
          this.catButton = "Edit your Area of Interest";
        } else {
          this.selectedCategories = [];
          this.otherCategories = [];
          this.catButton = "Select your Area of Interest";
        }
      })
      .catch((err) => console.error("Area of interest fetch error:", err));
  }


  /** Navigate to edit area of interest page */
  editAreaOfInterest() {
    this.router.navigate(["reg/area-of-interest"], {
      queryParams: { tab: "areaofinterest" },
    });
  }

  /**
   * View products of a specific category
   */
  viewProductsOfCategory(category: CategoryModel) {
    if (!this.isAuthenticated) {
      this.authService.saveStorageValue("state", "catalogue/pavilion-list");
      this.router.navigate(["/auth/init"]);
    } else {
      this.catalogueBllService.saveSelectedCategoryDetails(
        category.image_url,
        category.app_name_english
      );
      this.router.navigate(["catalogue/products-list"], {
        queryParams: { category_id: category.id },
      });
    }
  }
  /**
   * Handles the search input with debounce
   */
  onSearchInput() {
    // Clear previous timer
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(() => {
      if (this.searchText && this.searchText.trim() !== "") {
        const queryParams: any = { search: this.searchText.trim() };
        if (this.activeTab === "areaofinterest") {
          queryParams.tab = this.activeTab;
        }
        this.router.navigate(["/catalogue/products-list"], { queryParams });
      }
    }, 500); // 500ms debounce
  }
  /**
   * clear the debounce timer on component destroy
   */
  ngOnDestroy(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
  }

  registerVirtualVisitor() {
    this.virtualBll.submitRegisterVirtualVisitor().then(() => {
      // Registration successful
      this.hasRegisteredAsVirtualVisitor = true;
    }).catch((error) => {
      this.hasRegisteredAsVirtualVisitor = false;
    });
  }

  login() {
    this.authService.saveStorageValue("state", "catalogue/pavilion-list");
    this.router.navigate(["/auth/init"]);
  }

  viewLayout() {
    this.catalogueBllService.openLayout();
  }

  goToTicketPage() {
    this.router.navigate(["reg/select-ticket"]);
  }

  /**
   * Indicates whether the loader is currently shown.
   * Returns `true` if the loader service is active, otherwise `false`.
   */
  get isLoading(): boolean {
    return this.loader.isShown;
  }

  getRefSourceFromRoute() {
    const refSource =
      this.route.snapshot.queryParamMap.get("ref_source") || '';
    if (!refSource) {
      console.error("Ref source not found in route parameters");

    }
    this.homeBll.homeInitStorageChanges(undefined, refSource);
  }

  onClickVote(): void {
    this.router.navigate(['contests/participants-list']);
  }

  loadEventDetails() {
    this.homeBll.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;      
        this.isContestActive = this.eventDetails.is_contest_active;
        this.isVotingActive = this.eventDetails.is_voting_active;

         this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });
        
      })
      .catch((error) => {
        this.isVotingActive = false;
        this.isContestActive = false;
        console.error('Error fetching event details:', error);
      });
  }
}
